import React, { useEffect, useMemo, useState } from 'react'
import { getJwtToken } from '../../config/cognito'
import {
  getUsage,
  grantTopup,
  setUserTier,
  type UsageReport,
  type UsageUser
} from '../../api/askEvansApi'

function formatTokens(value: number) {
  if (value >= 1_000_000) {
    const millions = value / 1_000_000
    return `${millions >= 10 ? Math.round(millions) : millions.toFixed(1).replace(/\.0$/, '')}M`
  }
  if (value >= 1000) return `${Math.round(value / 1000)}k`
  return String(value)
}

function relativeTime(value?: string | null) {
  if (!value) return '—'
  const then = new Date(value).getTime()
  if (Number.isNaN(then)) return '—'
  const minutes = Math.round((Date.now() - then) / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`
  const days = Math.round(hours / 24)
  return days === 1 ? '1 day ago' : `${days} days ago`
}

function monthOptions() {
  const options: Array<{ value: string; label: string }> = []
  const now = new Date()
  for (let i = 0; i < 6; i += 1) {
    const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1))
    const value = date.toISOString().slice(0, 10)
    const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
    options.push({ value, label })
  }
  return options
}

function statusFor(percent: number) {
  if (percent >= 100) return 'Blocked'
  if (percent >= 80) return 'Near limit'
  return ''
}

export function AIUsagePanel() {
  const periods = useMemo(() => monthOptions(), [])
  const [period, setPeriod] = useState(periods[0]?.value || '')
  const [report, setReport] = useState<UsageReport | null>(null)
  const [tab, setTab] = useState<'allowance' | 'audit'>('allowance')
  const [error, setError] = useState<string | null>(null)
  const [tierUser, setTierUser] = useState<UsageUser | null>(null)
  const [tier, setTier] = useState('standard')
  const [topupUser, setTopupUser] = useState<UsageUser | null>(null)
  const [tokens, setTokens] = useState('100000')
  const [expiresAt, setExpiresAt] = useState('')

  function load(nextPeriod = period) {
    getUsage(nextPeriod)
      .then(setReport)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not load usage'))
  }

  useEffect(() => {
    load(period)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period])

  const users = report?.users || []
  const assistantTotal = users.reduce((sum, user) => sum + user.used, 0)

  return (
    <div className="space-y-4" data-testid="ai-usage">
      <div className="rounded-xl border border-[#E2E5EA] bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <label className="block text-sm font-medium text-blueprint-blue">
            Period
            <select
              aria-label="Period"
              className="mt-1 block rounded-lg border border-[#E2E5EA] px-3 py-2 text-sm"
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
            >
              {periods.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <p className="font-sans text-[13px] text-heritage-grey">
            {formatTokens(assistantTotal)} assistant tokens across {users.length} users. Allowances reset on the 1st of
            every month (UTC).
          </p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-[#E2E5EA]">
        <button
          type="button"
          className={`px-3 py-2 text-sm font-medium ${tab === 'allowance' ? 'border-b-2 border-hero-red text-blueprint-blue' : 'text-heritage-grey'}`}
          onClick={() => setTab('allowance')}
        >
          Allowance
        </button>
        <button
          type="button"
          className={`px-3 py-2 text-sm font-medium ${tab === 'audit' ? 'border-b-2 border-hero-red text-blueprint-blue' : 'text-heritage-grey'}`}
          onClick={() => setTab('audit')}
        >
          Ask Evans audit
        </button>
      </div>

      {error && <p className="text-sm text-readable-red">{error}</p>}

      {tab === 'allowance' ? (
        <div className="overflow-hidden rounded-xl border border-[#E2E5EA] bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E2E5EA] text-left">
              <thead className="bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Tier</th>
                  <th className="px-4 py-3">Assistant</th>
                  <th className="px-4 py-3">Last used</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3]">
                {users.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-heritage-grey">
                      No Ask Evans usage in this period.
                    </td>
                  </tr>
                )}
                {users.map((user) => {
                  const status = statusFor(user.percent)
                  return (
                    <tr key={user.userSub} className="hover:bg-page-bg">
                      <td className="px-4 py-3">
                        <span className="block text-sm font-medium text-blueprint-blue">
                          {user.displayName || user.email || user.userSub}
                        </span>
                        <span className="block text-[11px] text-heritage-grey">{user.email || user.userSub}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-page-bg px-2 py-0.5 text-[11px] capitalize text-heritage-grey">
                          {user.tier}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="min-w-[160px]">
                          <div className="mb-1 flex items-baseline justify-between gap-2 text-sm text-text-grey">
                            <span>
                              {formatTokens(user.used)} of {formatTokens(user.limit)}
                            </span>
                            <span className="text-[11px] text-heritage-grey">{user.percent}%</span>
                          </div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-[#EEF0F3]">
                            <div
                              className={`h-full rounded-full ${user.percent >= 100 ? 'bg-hero-red' : user.percent >= 80 ? 'bg-thin-yellow' : 'bg-blueprint-blue'}`}
                              style={{ width: `${Math.min(100, user.percent)}%` }}
                            />
                          </div>
                          {status && (
                            <span
                              className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                status === 'Blocked' ? 'bg-hero-red/10 text-readable-red' : 'bg-[#FFCA57]/30 text-[#8A6500]'
                              }`}
                            >
                              {status}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-heritage-grey">{relativeTime(user.lastUsedAt)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          className="mr-2 rounded-lg border border-[#E2E5EA] px-3 py-1.5 text-[12px] text-blueprint-blue"
                          onClick={() => {
                            setTierUser(user)
                            setTier(user.tier)
                          }}
                        >
                          Change tier
                        </button>
                        <button
                          type="button"
                          className="rounded-lg border border-[#E2E5EA] px-3 py-1.5 text-[12px] text-blueprint-blue"
                          onClick={() => setTopupUser(user)}
                        >
                          Grant top-up
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[#E2E5EA] bg-white">
          <table className="min-w-full divide-y divide-[#E2E5EA] text-left text-sm">
            <thead className="bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
              <tr>
                <th className="px-4 py-3">Question</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Rounds</th>
                <th className="px-4 py-3">Tools</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EEF0F3]">
              {(report?.jobs || []).map((job) => (
                <tr key={job.id}>
                  <td className="max-w-md truncate px-4 py-3 text-blueprint-blue">{job.question}</td>
                  <td className="px-4 py-3 capitalize text-heritage-grey">{job.status}</td>
                  <td className="px-4 py-3 text-heritage-grey">{job.roundsUsed}</td>
                  <td className="px-4 py-3 text-heritage-grey">{job.toolsCalled}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button
        type="button"
        className="text-sm font-medium text-blueprint-blue underline"
        onClick={() => {
          void (async () => {
            const token = await getJwtToken()
            const base = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '')
            const res = await fetch(`${base}/api/ask-evans/admin/usage.csv`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {}
            })
            const blob = await res.blob()
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = 'ask-evans-usage.csv'
            link.click()
            URL.revokeObjectURL(url)
          })()
        }}
      >
        Download CSV
      </button>

      {tierUser && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">Change tier</h3>
            <p className="mt-1 text-[12px] text-heritage-grey">{tierUser.email || tierUser.userSub}</p>
            <select aria-label="Tier" className="mt-3 w-full rounded-lg border border-[#E2E5EA] px-3 py-2 text-sm" value={tier} onChange={(event) => setTier(event.target.value)}>
              <option value="standard">Standard</option>
              <option value="plus">Plus</option>
              <option value="max">Max</option>
            </select>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="rounded-lg px-3 py-2 text-sm text-heritage-grey" onClick={() => setTierUser(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-hero-red px-3 py-2 text-sm text-white"
                onClick={() => {
                  void setUserTier(tierUser.userSub, tier).then(() => {
                    setTierUser(null)
                    load()
                  })
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {topupUser && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">Grant top-up</h3>
            <p className="mt-1 text-[12px] text-heritage-grey">{topupUser.email || topupUser.userSub}</p>
            <label className="mt-3 block text-sm text-blueprint-blue">
              Tokens
              <input
                aria-label="Top-up tokens"
                className="mt-1 w-full rounded-lg border border-[#E2E5EA] px-3 py-2"
                value={tokens}
                onChange={(event) => setTokens(event.target.value)}
              />
            </label>
            <label className="mt-3 block text-sm text-blueprint-blue">
              Expires
              <input
                aria-label="Top-up expiry"
                type="date"
                className="mt-1 w-full rounded-lg border border-[#E2E5EA] px-3 py-2"
                value={expiresAt}
                onChange={(event) => setExpiresAt(event.target.value)}
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="rounded-lg px-3 py-2 text-sm text-heritage-grey" onClick={() => setTopupUser(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-hero-red px-3 py-2 text-sm text-white"
                onClick={() => {
                  if (!expiresAt) return
                  void grantTopup(topupUser.userSub, Number(tokens), new Date(`${expiresAt}T23:59:59.000Z`).toISOString()).then(() => {
                    setTopupUser(null)
                    load()
                  })
                }}
              >
                Grant
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
